import { useCallback, useEffect, useState } from "react";
import { Loader2, MapPin, Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import ChangeHistoryPanel from "@/components/bulk/ChangeHistoryPanel";
import PageHeader from "@/components/PageHeader";

export default function AttendanceSettings() {
  const [form, setForm] = useState(null);
  const [orig, setOrig] = useState(null);
  const [busy, setBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [zones, setZones] = useState([]);

  const load = useCallback(async () => {
    const [{ data: s }, { data: z }] = await Promise.all([
      api.get("/attendance/settings"),
      api.get("/zones", { params: { page_size: 100 } }),
    ]);
    setForm(s); setOrig(s); setZones(z.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!form) return <div className="p-6 text-neutral-500">Loading…</div>;
  const dirty = JSON.stringify(form) !== JSON.stringify(orig);

  const patch = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const patchCentroid = (code, key, val) => setForm((s) => ({
    ...s, zone_centroids: {
      ...(s.zone_centroids || {}),
      [code]: { ...(s.zone_centroids?.[code] || {}), [key]: val === "" ? null : Number(val) },
    },
  }));

  const grabCurrentLocation = (code) => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    navigator.geolocation.getCurrentPosition(
      (p) => patchCentroid(code, "lat", p.coords.latitude) || patchCentroid(code, "lng", p.coords.longitude),
      (e) => toast.error(e.message),
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/attendance/settings", form);
      setForm(data); setOrig(data);
      toast.success("Attendance settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-6 space-y-4" data-testid="admin-attendance-settings-page">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Masters" }, { label: "Attendance settings" }]}
        title="Attendance settings"
        subtitle="GPS radius, selfie requirements, and punch-in enforcement."
        favorite={{ key: "admin.attendance-settings", type: "screen", label: "Attendance settings", route: "/admin/attendance-settings" }}
        testid="attendancesettings-page-header"
      />

      <div>
        <h1 className="text-2xl font-bold text-neutral-900">Attendance settings</h1>
        <p className="text-sm text-neutral-500">
          Applies immediately to any punch-in / punch-out. All fields are no-code — the field app reads them at every capture.
        </p>
      </div>

      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-5 space-y-4">
          <ToggleRow label="GPS required" hint="Field app blocks punch when GPS is unavailable."
                     checked={form.gps_required} onCheckedChange={(v) => patch("gps_required", v)}
                     testid="settings-gps-required" />
          <ToggleRow label="Selfie required" hint="Requires a selfie for both punch-in and punch-out."
                     checked={form.selfie_required} onCheckedChange={(v) => patch("selfie_required", v)}
                     testid="settings-selfie-required" />
          <ToggleRow label="Checkout required" hint="If off, unpunched-out records stay 'partial' and don't block next-day punch-in."
                     checked={form.checkout_required} onCheckedChange={(v) => patch("checkout_required", v)}
                     testid="settings-checkout-required" />

          <div className="grid grid-cols-3 gap-4 pt-2">
            <FieldNum label="Beat radius (m)" value={form.beat_radius_m}
                      min={50} max={5000}
                      onChange={(v) => patch("beat_radius_m", v)} testid="settings-beat-radius" />
            <FieldNum label="Grace before (min)" value={form.punch_grace_start_minutes}
                      min={0} max={180}
                      onChange={(v) => patch("punch_grace_start_minutes", v)} testid="settings-grace-start" />
            <FieldNum label="Grace after (min)" value={form.punch_grace_end_minutes}
                      min={0} max={180}
                      onChange={(v) => patch("punch_grace_end_minutes", v)} testid="settings-grace-end" />
          </div>
        </CardContent>
      </Card>

      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-5 space-y-3">
          <div className="text-xs uppercase tracking-widest text-neutral-500">Zone centroids (fallback for radius)</div>
          <div className="text-[11px] text-neutral-500">Used when a punch-in isn't tied to a specific beat.</div>
          <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end text-xs">
            <Label className="text-[10px] uppercase tracking-widest text-neutral-500">Zone</Label>
            <Label className="text-[10px] uppercase tracking-widest text-neutral-500">Lat</Label>
            <Label className="text-[10px] uppercase tracking-widest text-neutral-500">Lng</Label>
            <span />
            {zones.map((z) => {
              const c = form.zone_centroids?.[z.code] || {};
              return (
                <div key={z.code} className="col-span-4 grid grid-cols-[1fr_1fr_1fr_auto] gap-2"
                     data-testid={`settings-centroid-row-${z.code}`}>
                  <div className="text-sm font-semibold text-neutral-900 self-center">{z.code} · {z.name}</div>
                  <Input type="number" step="any" value={c.lat ?? ""} onChange={(e) => patchCentroid(z.code, "lat", e.target.value)}
                         data-testid={`settings-centroid-lat-${z.code}`} />
                  <Input type="number" step="any" value={c.lng ?? ""} onChange={(e) => patchCentroid(z.code, "lng", e.target.value)}
                         data-testid={`settings-centroid-lng-${z.code}`} />
                  <Button size="sm" variant="outline" onClick={() => grabCurrentLocation(z.code)}
                          data-testid={`settings-centroid-mylocation-${z.code}`}>
                    <MapPin className="h-3 w-3 mr-1" /> Use current
                  </Button>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-2">
        <Button onClick={save} disabled={busy || !dirty}
                className="tulsi-gradient text-white font-semibold"
                data-testid="settings-save">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save changes
        </Button>
        <Button variant="outline" onClick={() => setForm(orig)} disabled={!dirty}
                data-testid="settings-reset">
          <RotateCcw className="h-4 w-4 mr-2" /> Reset
        </Button>
        <Button variant="ghost" onClick={() => setHistoryOpen(true)} data-testid="settings-history">
          Change history
        </Button>
        {dirty && <span className="text-[11px] text-amber-700">Unsaved changes — effective on Save</span>}
        {!dirty && <span className="text-[11px] text-neutral-500">Changes take effect for new punches immediately</span>}
      </div>

      <ChangeHistoryPanel
        open={historyOpen} onOpenChange={setHistoryOpen}
        entityType="attendance_settings" entityId="default"
        title="Attendance settings — history"
      />
    </div>
  );
}

function ToggleRow({ label, hint, checked, onCheckedChange, testid }) {
  return (
    <div className="flex items-center justify-between border-b border-neutral-100 pb-3 last:border-0 last:pb-0">
      <div>
        <div className="text-sm font-semibold text-neutral-900">{label}</div>
        <div className="text-[11px] text-neutral-500">{hint}</div>
      </div>
      <Switch checked={!!checked} onCheckedChange={onCheckedChange} data-testid={testid} />
    </div>
  );
}

function FieldNum({ label, value, min, max, onChange, testid }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</Label>
      <Input type="number" min={min} max={max} value={value ?? ""}
             onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
             data-testid={testid} />
    </div>
  );
}
